import {
  API_BASE_URL,
  parseIstatecResponse,
  parseValidationError,
  type IstatecResponse,
} from './apiConfig';

export type { IstatecResponse };

export interface SendOtpPayload {
  name: string;
  email: string;
  require_work_email?: boolean;
}

export interface ValidateOtpPayload {
  name: string;
  work_email: string;
  personal_email: string;
  company: string;
  phone_number: string;
  service_interested_in: string;
  /**
   * User's relationship with Istatec (Client/Prospect, Partner/Alliance,
   * Investor/Analyst, Media/Journalist, Others). Optional for backwards
   * compatibility with older backends; the API server treats it as
   * optional and falls back to "NA" in the admin email.
   */
  relationship?: string;
  /**
   * Industry the visitor operates in (Banking and Financial Services,
   * Healthcare and Life Sciences, Manufacturing, Public Sector, etc. —
   * or "Other / Not Sure"). Same optional / "NA" fallback semantics as
   * `relationship` so older clients keep working.
   */
  industry?: string;
  message: string;
  otp: string;
}

/**
 * Step 1 — POST /send_otp_email_validation
 * For the Contact-Us flow the caller should pass `require_work_email: true`
 * so personal-provider domains are rejected before the OTP is generated.
 */
export async function sendOtp(payload: SendOtpPayload): Promise<IstatecResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}/send_otp_email_validation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422) return parseValidationError(res);
    return parseIstatecResponse(res);
  } catch {
    return {
      status: 'error',
      message: 'Network error. Please check your connection and try again.',
      status_code: 0,
      data: null,
      request_id: '',
    };
  }
}

/**
 * Step 2 — POST /validate_otp_and_send_email
 * OTP is verified against `work_email`. `personal_email` is metadata that is
 * forwarded to the admin inquiry email.
 */
export async function validateOtpAndSubmit(payload: ValidateOtpPayload): Promise<IstatecResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}/validate_otp_and_send_email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422) return parseValidationError(res);
    return parseIstatecResponse(res);
  } catch {
    return {
      status: 'error',
      message: 'Network error. Please try again.',
      status_code: 0,
      data: null,
      request_id: '',
    };
  }
}
